import { useCallback, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { SuggestionProduct } from '@atlassian/navigation-bff-data-provider';

import {
	ANALYTICS_CHANNEL,
	ANALYTICS_SOURCE,
	ANALYTICS_TAG,
	NAV_CONTAINER,
	NAV_LAYER,
	NAV_VERSION,
} from '../constants';
import { useMenuControl } from '../context';
import type { SourceProduct, SuggestionConfigEnabled } from '../types';

export interface MenuContentViewedAttributes {
	crossAppSuggestionsEnabled: boolean;
	nativeSections: Array<{
		itemAtis: string[];
		itemCount: number;
		sectionHeading?: string;
		sectionIndex: number;
		sectionType: string;
	}>;
	totalNativeItems: number;
	totalNativeSections: number;
}

export interface MenuItemClickedAttributes {
	ati: string;
	cloudId?: string;
	sourceProduct?: SourceProduct;
	suggestionProduct?: SuggestionProduct;
}

export interface SuggestionMenuViewedAttributes {
	suggestedItems: string[];
	suggestionsCount: number;
}
export interface CreateButtonClickedAttributes {
	itemState: 'expanded' | 'collapsed';
}

export interface NewMenuItemClickedAttributes {
	ati: string; // Atlassian Type Identifier of the item
	groupItemProduct: SuggestionProduct | SourceProduct; // Product item is natively associated with
	groupItemsCount: number; // Number of items in the section
	groupItemsIndex: number; // Position of the item in the section
	isRightClick: boolean; // True if event triggered by right click opening context menu
	menuItemIndex: number; // Position of the item in the entire native sections part of menu (not just section)
	menuItemsCount: number; // Total number of items in all native sections of menu
}

export interface NewNativeMenuViewedAttributes {
	crossAppSuggestionsEnabled: boolean;
	nativeMenuGroups: Array<{
		groupHeading: string;
		groupIndex: number; // Index of section out of all sections
		groupItemCount: number; // total number of items in the section
		itemAtis: string[];
		showMoreButton: boolean; // true if items in section have been truncated with show more button
	}>;
	totalGroups: number; // Total count of native sections in the menu
	totalNativeItems: number; // Total count of items in all native sections
}

export interface NewSuggestionsMenuViewedAttributes {
	crossAppSuggestionsEnabled: boolean;
	suggestionMenuGroup: {
		displayModeCompact: SuggestionConfigEnabled['compact']; // Configuration of whether 'Suggestion/s' heading is hidden or visible
		itemAtis: string[];
		showMoreButton: boolean; // true if items in section have been truncated with show more button
		suggestionLocation: SuggestionConfigEnabled['location']; // Whether suggestions are shown at start (top) or end of menu (below native items)
	};
	totalSuggestionItems: number; // Total count of items in suggestions section
}

export interface UniversalCreateAnalyticsEvents {
	// Create button click events
	fireCreateButtonClicked: (attributes: CreateButtonClickedAttributes) => void;

	// Native menu content viewed events
	fireMenuContentViewed: (attributes: MenuContentViewedAttributes) => void;

	// Universal create menu expanded or collapsed via any exit action
	fireMenuStateChanged: (attributes: CreateButtonClickedAttributes) => void;

	// Native menu item clicked events
	fireNativeMenuItemClicked: (attributes: MenuItemClickedAttributes) => void;

	// Native Menu item click events
	fireNewNativeMenuItemClicked: (attributes: NewMenuItemClickedAttributes) => void;

	// Native Menu items viewed events
	fireNewNativeMenuViewed: (attributes: NewNativeMenuViewedAttributes) => void;

	// Original events below

	// Suggestions Menu item click events
	fireNewSuggestionsMenuItemClicked: (attributes: NewMenuItemClickedAttributes) => void;

	// Suggestions Menu items viewed events
	fireNewSuggestionsMenuViewed: (attributes: NewSuggestionsMenuViewedAttributes) => void;

	// Remote menu item clicked events
	fireRemoteMenuItemClicked: (attributes: MenuItemClickedAttributes) => void;

	// Suggestion menu viewed events
	fireSuggestionMenuViewed: (attributes: SuggestionMenuViewedAttributes) => void;

	// Trigger button events
	fireTriggerButtonClicked: () => void;
}

/**
 * Custom hook for Universal Create analytics events
 * Provides type-safe, consistent analytics event firing across all UC components
 */
export function useUniversalCreateAnalytics(): UniversalCreateAnalyticsEvents {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { product, subProduct } = useMenuControl();

	// Provide fallback for product if undefined
	const safeProduct = product || 'unknown';

	const baseAttributes = useMemo(
		() => ({
			navLayer: NAV_LAYER,
			navContainer: NAV_CONTAINER,
			navVersion: NAV_VERSION,
			product: safeProduct,
			subProduct,
		}),
		[safeProduct, subProduct],
	);

	const topLevelAttributes = useMemo(
		() => ({
			actionSubject: 'universalCreate',
			eventType: 'ui',
			source: ANALYTICS_SOURCE,
			tags: [ANALYTICS_TAG],
		}),
		[],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88038
	const fireCreateButtonClicked = useCallback(
		(attributes: CreateButtonClickedAttributes) => {
			createAnalyticsEvent({
				action: 'clicked',
				actionSubjectId: 'universalCreateButton',
				...topLevelAttributes,
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88332
	const fireNewNativeMenuItemClicked = useCallback(
		(attributes: NewMenuItemClickedAttributes) => {
			createAnalyticsEvent({
				action: 'clicked',
				actionSubjectId: 'universalCreateNativeMenuItem',
				...topLevelAttributes,
				attributes: {
					group: 'native',
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88486
	const fireNewSuggestionsMenuItemClicked = useCallback(
		(attributes: NewMenuItemClickedAttributes) => {
			createAnalyticsEvent({
				action: 'clicked',
				actionSubjectId: 'universalCreateSuggestionsMenuItem',
				...topLevelAttributes,
				attributes: {
					group: 'suggestions',
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88599
	const fireNewNativeMenuViewed = useCallback(
		(attributes: NewNativeMenuViewedAttributes) => {
			createAnalyticsEvent({
				action: 'viewed',
				actionSubjectId: 'universalCreateNativeMenu',
				...topLevelAttributes,
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88724
	const fireNewSuggestionsMenuViewed = useCallback(
		(attributes: NewSuggestionsMenuViewedAttributes) => {
			createAnalyticsEvent({
				action: 'viewed',
				actionSubjectId: 'universalCreateSuggestionsMenu',
				...topLevelAttributes,
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/89698
	const fireMenuStateChanged = useCallback(
		(attributes: CreateButtonClickedAttributes) => {
			createAnalyticsEvent({
				action: 'changed',
				actionSubjectId: 'universalCreateMenuState',
				...topLevelAttributes,
				attributes: {
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes],
	);

	// Original events below

	const nativeSubject = 'UCMenuNativeContent';

	const fireMenuContentViewed = useCallback(
		(attributes: MenuContentViewedAttributes) => {
			createAnalyticsEvent({
				action: 'viewed',
				actionSubject: nativeSubject,
				name: nativeSubject,
				eventType: 'screen',
				source: ANALYTICS_SOURCE,
				tags: [ANALYTICS_TAG],
				attributes: {
					...attributes,
					product: safeProduct,
					subProduct,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, safeProduct, subProduct],
	);

	const suggestionsSubject = 'UCMenuSuggestions';

	const fireSuggestionMenuViewed = useCallback(
		(attributes: SuggestionMenuViewedAttributes) => {
			createAnalyticsEvent({
				action: 'viewed',
				actionSubject: suggestionsSubject,
				name: suggestionsSubject,
				eventType: 'screen',
				source: ANALYTICS_SOURCE,
				tags: [ANALYTICS_TAG],
				attributes: {
					...attributes,
					product: safeProduct,
					subProduct,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, subProduct, safeProduct],
	);

	const nativeItemSubject = 'UCMenuNativeItem';

	const fireNativeMenuItemClicked = useCallback(
		(attributes: MenuItemClickedAttributes) => {
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: nativeItemSubject,
				eventType: 'ui',
				source: ANALYTICS_SOURCE,
				tags: [ANALYTICS_TAG],
				attributes: {
					...attributes,
					product: safeProduct,
					subProduct,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, safeProduct, subProduct],
	);

	const remoteItemSubject = 'RemoteUCMenuItem';

	const fireRemoteMenuItemClicked = useCallback(
		(attributes: MenuItemClickedAttributes) => {
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: remoteItemSubject,
				eventType: 'ui',
				source: ANALYTICS_SOURCE,
				tags: [ANALYTICS_TAG],
				attributes: {
					...attributes,
					product: safeProduct,
					subProduct,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, safeProduct, subProduct],
	);

	const fireTriggerButtonClicked = useCallback(() => {
		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'universalCreateTriggerButton',
			eventType: 'ui',
			source: ANALYTICS_SOURCE,
			tags: [ANALYTICS_TAG],
			attributes: {
				product: safeProduct,
				subProduct,
			},
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, safeProduct, subProduct]);

	return {
		fireCreateButtonClicked, // new
		fireNewNativeMenuItemClicked, // new
		fireNewSuggestionsMenuItemClicked, // new
		fireNewNativeMenuViewed, // new
		fireNewSuggestionsMenuViewed, // new
		fireMenuStateChanged, // new
		fireMenuContentViewed,
		fireNativeMenuItemClicked,
		fireRemoteMenuItemClicked,
		fireSuggestionMenuViewed,
		fireTriggerButtonClicked,
	};
}
